import OrderItemPromotionAlert from '@/modules/order/components/OrderItemPromotionAlert';
import { Amount } from '@/modules/order/models/Amount';
import { OrderItemPromotion } from '@/modules/order/models/OrderItem';
import { isOrderAccepted, isOrderRejected, OrderStatus } from '@/modules/order/models/OrderStatus';
import { formatPrice } from '@/utils/currency';
import { getTaxLabels } from '@/utils/tax';

interface OrderItemTableOrder {
  status: OrderStatus;
  items: OrderItemTableItem[];
  subtotal?: Amount;
  grandTotal?: Amount;
}

interface OrderItemTableItem {
  id: string;
  lineNumber: number;
  skuName: string;
  promotions?: OrderItemPromotion[];
  skuCode: string;
  quantity: number;
  confirmedQuantity?: number;
  vatRate?: number;
  lineItemPrice?: Amount;
}

interface OrderItemTableProps {
  order: OrderItemTableOrder;
  countryCode?: string;
  showVat?: boolean;
  hidePriceData?: boolean;
}

const OrderItemTable = ({
  order,
  countryCode,
  showVat = true,
  hidePriceData = false,
}: OrderItemTableProps) => {
  const taxLabels = getTaxLabels(countryCode);
  const isAccepted = isOrderAccepted(order);
  const shouldShowConfirmedQuantity = isAccepted || isOrderRejected(order);

  const totalColumns =
    4 +
    (shouldShowConfirmedQuantity ? 1 : 0) +
    (!hidePriceData && showVat ? 1 : 0) +
    (!hidePriceData ? 1 : 0);

  return (
    <div className="shadow-md rounded-lg">
      <div className="relative overflow-x-auto border rounded-t-lg">
        <table className="min-w-full" data-testid="order-items-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-300 text-gray-500 font-medium text-sm">
              <th className="px-6 py-3 pr-3 h-12 w-20 text-left">#</th>
              <th className="px-6 py-3 text-left w-96">SKU</th>
              <th className="px-6 py-3 text-left w-40">SKU Code</th>
              <th className="px-6 py-3 text-left">Ordered Quantity</th>
              {shouldShowConfirmedQuantity && (
                <th className="px-6 py-3 text-left">Confirmed Quantity</th>
              )}
              {!hidePriceData && (
                <>
                  {showVat && <th className="px-6 py-3 text-left">{taxLabels.rate}</th>}
                  <th className="px-3 py-3 pr-6 text-right w-40">{taxLabels.subtotalExcluding}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {order.items.map((orderItem) => {
              const confirmedQuantity =
                isAccepted && orderItem.confirmedQuantity === undefined
                  ? orderItem.quantity
                  : orderItem.confirmedQuantity;

              return (
                <tr
                  key={orderItem.id}
                  className="text-sm lg:text-base"
                  data-testid="order-item-row"
                  data-sku-code={orderItem.skuCode}
                  data-sku-name={orderItem.skuName}
                >
                  <td className="px-6 py-4">{orderItem.lineNumber}</td>
                  <td className="px-6 py-4" data-testid="order-item-name">
                    {orderItem.skuName}

                    {(orderItem.promotions?.length || 0) > 0 && (
                      <div className="mt-1 space-y-1">
                        <OrderItemPromotionAlert item={orderItem} />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4" data-testid="order-item-sku-code">
                    {orderItem.skuCode}
                  </td>
                  <td className="px-6 py-4" data-testid="order-item-ordered-quantity">
                    {orderItem.quantity}
                  </td>
                  {shouldShowConfirmedQuantity && (
                    <td className="px-6 py-4" data-testid="order-item-confirmed-quantity">
                      {confirmedQuantity}
                    </td>
                  )}
                  {!hidePriceData && (
                    <>
                      {showVat && <td className="px-6 py-4">{(orderItem.vatRate ?? 0) * 100}%</td>}
                      <td className="px-3 py-4 pr-6 text-right">
                        {formatPrice(
                          orderItem.lineItemPrice,
                          orderItem.lineItemPrice?.currencyCode,
                        )}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
          {!hidePriceData && (
            <tbody className="border-t border-gray-300">
              <tr>
                <td
                  colSpan={totalColumns - 1}
                  className="text-right px-3 pr-1 py-1 pt-6 font-semibold text-sm"
                >
                  {taxLabels.subtotalExcluding}
                </td>
                <td className="px-3 pt-6 pr-6 text-right">
                  {formatPrice(order?.subtotal, order.subtotal?.currencyCode)}
                </td>
              </tr>
              <tr>
                <td
                  colSpan={totalColumns - 1}
                  className={`text-right px-3 pr-1 py-1 font-semibold text-sm ${
                    !showVat ? 'pb-6' : ''
                  }`}
                >
                  {taxLabels.totalExcluding}
                </td>
                <td className={`px-3 py-1 pr-6 text-right ${!showVat ? 'pb-6' : ''}`}>
                  {formatPrice(order?.subtotal, order.subtotal?.currencyCode)}
                </td>
              </tr>
              {showVat && (
                <tr>
                  <td
                    colSpan={totalColumns - 1}
                    className="text-right px-3 pr-1 py-1 pb-6 font-semibold text-sm"
                  >
                    {taxLabels.grandTotalIncluding}
                  </td>
                  <td className="px-3 py-1 pb-6 pr-6 text-right">
                    {formatPrice(order?.grandTotal, order.grandTotal?.currencyCode)}
                  </td>
                </tr>
              )}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default OrderItemTable;
